import type { CoreTypes } from '../../../core';
import type { PUBLIC_ID_KEY } from '../../../core/interfaces/vertices/util/vertex';
import type { Schedule } from '../types/schedule';

const sep = ',';
function split (val: string): string[] {
  return val.split(sep)
    .map(x => x.trim())
    .filter(x => x.length);
}

function courseMapKey (
  subject:  string | undefined,
  groups:   string[],
  teachers: string[]
): string {
  return JSON.stringify({ s: subject, g: groups.sort(), t: teachers.sort() });
}

namespace OutTypes {
  export type event    = CoreTypes.SerializedWithOptionalId.Event    & { [PUBLIC_ID_KEY]: string };
  export type course   = CoreTypes.SerializedWithOptionalId.Course   & { [PUBLIC_ID_KEY]: string };
  export type location = CoreTypes.SerializedWithOptionalId.Location & { [PUBLIC_ID_KEY]: string };
  export type group    = CoreTypes.SerializedWithOptionalId.Group    & { [PUBLIC_ID_KEY]: string };
  export type person   = CoreTypes.SerializedWithOptionalId.Person   & { [PUBLIC_ID_KEY]: string };
  export type teacher  = CoreTypes.SerializedWithOptionalId.Teacher  & { [PUBLIC_ID_KEY]: string };
}
type Out = {
  locations: OutTypes.location[];
  teachers:  OutTypes.teacher[];
  groups:    OutTypes.group[];
  courses:   OutTypes.course[];
  events:    OutTypes.event[];
  persons:   OutTypes.person[];

  meta: {
    errors?:   string[];
    warnings?: string[];
  };
};

export default (
  data: Schedule
): Out => {

  // gather all import related warnings/errors
  const errors:   string[] = [];
  const warnings: string[] = [];

  ////
  //// persons
  ////
  const persons = data.persons
    .map((x): OutTypes.person => ({
      ids:       x.id,
      //group:       x.class, only name of class
      firstName: x.fname,
      lastName:  x.lname,
      sex:       ['m', 'p'].includes(x.sex) ? 'Man' : (['f', 'w'].includes(x.sex) ? 'Woman' : undefined),
      ...x.socialnumber && {
        SSN: {
          value:       x.socialnumber,
          nationality: 'SE'
        }
      },
      ...x.email && {
        emails: [
          {
            value: x.email,
            type:  'organization'
          }
        ]
      }
    }));

  ////
  //// locations
  ////
  const locations = data.locations
    .map((x): OutTypes.location => ({
      ids:         x.id,
      displayName: x.name
    }));


  ////
  //// teachers
  ////
  const initialTeacherMap = new Map(data.teachers
    .map((x): undefined | [string, OutTypes.teacher] => {
      // must be active
      if (x.active != '1') return;

      // ensure the initials to be unique
      if (initialTeacherMap.has(x.initial)) {
        warnings.push(`There already exists an active teacher with the initial "${x.initial}".`);
        return;
      }

      return [x.initial, {
        ids:         x.id,
        displayName: x.fname + ' ' + x.lname
      }];
    })
    .filter(x => x != null)
  );



  ////
  //// groups
  ////
  const studentGroupMap = new Map<string, string>();
  const nameGroupMap = new Map<string, OutTypes.group>();
  data.groups.forEach(x => {
    const name = x.name.trim();

    // must be active
    if (x.active != '1') return;

    // must be a class type
    if (x.classtype != '1') return;

    // ensure the name to be unique
    if (nameGroupMap.has(name)) {
      warnings.push(`There already exists an active group with the name "${name}".`);
      return;
    }

    // add to student group map
    const members = split(x.studentid).filter(x => x);

    members.forEach(id => {
      if (studentGroupMap.has(id)) {
        errors.push(`The student with id "${id}" does already belong to a different group.`);
        return;
      }

      studentGroupMap.set(id, x.id);
    });

    // add group
    nameGroupMap.set(name, {
      ids:         x.id,
      displayName: name,
      species:     'class',
      members
    });
  });


  ////
  //// create "scheduled group" to subject map
  ////
  const groupSubjectMap = new Map<string, string>();
  data.lessons.forEach(x => {
    if (x.subject) {
      groupSubjectMap.set(x.group.trim(), x.subject);
    }
  });


  ////
  //// courses
  ////
  const courseMap     = new Map<string, OutTypes.course>();
  const nameCourseMap = new Map<string, OutTypes.course>();
  data.groups.forEach(x => {
    const name = x.name.trim();

    // must be active
    if (x.active != '1') return;

    // must be a course type
    if (x.classtype == '1') return;

    // find all groups connected to the course
    const groupSet = new Set<string>();
    split(x.studentid)
      .filter(x => x)
      .map(id => {
        const group = studentGroupMap.get(id);
        if (!group) {
          errors.push(`The student with id "${id}" does not belong to a group.`);
          return;
        }
        groupSet.add(group);
      });

    // find all teachers
    const teacherSet = new Set<string>();
    split(x.teacher)
      .filter(x => x)
      .map(initial => {
        const teacher = initialTeacherMap.get(initial);
        if (!teacher) {
          errors.push(`The teacher with initial "${initial}" does not exist.`);
          return;
        }

        teacherSet.add(teacher.ids);
      });

    // try to figure out the subject from the group name via its lessons
    const subject = groupSubjectMap.get(name);
    if (!subject) {
      warnings.push(`Could not determine the subject of the course "${name}" (${x.id}).`);
    }


    // ensure the name to be unique
    if (nameGroupMap.has(name)) {
      warnings.push(`There already exists an active group with the name "${name}".`);
      return;
    }

    // add course
    const course: OutTypes.course = {
      ids:         x.id,
      displayName: name,
      // groups:      [...groupSet].map(to => ({ to })),
      // teachers:    [...teacherSet].map(to => ({ to })),
      ...(subject && { subject })
    };
    nameCourseMap.set(name, course);
    courseMap.set(courseMapKey(course.subject, [...groupSet], [...teacherSet]), course);
  });


  ////
  //// load further courses from lessons
  ////
  data.lessons.forEach(x => {

    // fetch its teachers
    const teacherSet = new Set<string>();
    split(x.teacher)
      .forEach(y => {
        const id = initialTeacherMap.get(y)?.ids;
        if (id) teacherSet.add(id);
      });
    const teachers = [...teacherSet].map(to => ({ to }));

    // fetch its groups
    const scheduledGroupIds: string[] = [];
    const groupSet = new Set<string>();
    split(x.group)
      .forEach(y => {
      // is group
        const id = nameGroupMap.get(y)?.ids;
        if (id) {
          scheduledGroupIds.push(id);
          groupSet.add(id);
          return;
        }

        // is course
        const course = nameCourseMap.get(y);
        if (course) {
          scheduledGroupIds.push(course.ids);
          course.groups?.forEach(z => groupSet.add(z.to.toString()));
        }
      });
    const groups = [...groupSet].map(to => ({ to }));

    // add as course if at least a single groups or teacher is connected
    const key = courseMapKey(x.subject, groups.map(({ to }) => to), teachers.map(({ to }) => to));
    if (groups.length && teachers.length && !courseMap.has(key)) {
      const ids = scheduledGroupIds.join(sep);
      courseMap.set(key, {
        ids:         ids,
        displayName: x.subject + ( x.group.length ? ' - ' + x.group : '') + ( x.teacher.length ? ' - ' + x.teacher : ''),
        subject:     x.subject,
        groups:      [], // Don't assign to course
        teachers:    [], // Don't assign to course
      });
    }

  });


  const teachers = [...initialTeacherMap.values()];
  const groups   = [...nameGroupMap.values()];
  const courses  = [...courseMap.values()];
  const events: OutTypes.event[] = [];
  return { locations, teachers, groups, courses, events, persons,
    meta: {
      ...( errors.length   ? { errors:   errors   } : { } ),
      ...( warnings.length ? { warnings: warnings } : { } )
    }
  };
};